import {
  useCallback,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { ChevronsLeftRight } from 'lucide-react';
import { cn } from '../../../lib/cn.js';
import { resolveAssetUrl } from './image-source.js';

// Draggable before/after comparison slider. Pure React + CSS (clip-path), no
// dependency. Pointer (mouse + touch via Pointer Events) and keyboard
// accessible. Technique ported from public/blog-demos/bg-removal-slider.html.
// The divider follows the pointer instantly (no transition), so it is usable
// under prefers-reduced-motion without any special-casing.

const clamp = (n: number): number => Math.max(0, Math.min(100, n));

export interface BeforeAfterProps {
  before: string;
  after: string;
  beforeAlt: string;
  afterAlt: string;
  beforeLabel?: string;
  afterLabel?: string;
  /** Initial divider position, 0–100. Default 50. */
  initial?: number;
  /** CSS aspect-ratio of the frame. Default "1 / 1". */
  aspectRatio?: string;
  caption?: string;
  /** Checkerboard behind the AFTER (base) image, for transparent cut-outs. */
  transparentAfter?: boolean;
  className?: string;
}

export function BeforeAfter({
  before,
  after,
  beforeAlt,
  afterAlt,
  beforeLabel = 'Before',
  afterLabel = 'After',
  initial = 50,
  aspectRatio = '1 / 1',
  caption,
  transparentAfter = false,
  className,
}: BeforeAfterProps): JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [pos, setPos] = useState(() => clamp(initial));

  const beforeUrl = resolveAssetUrl(before);
  const afterUrl = resolveAssetUrl(after);

  const setFromClientX = useCallback((clientX: number) => {
    const el = frameRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return;
    setPos(clamp(((clientX - rect.left) / rect.width) * 100));
  }, []);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      draggingRef.current = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setFromClientX(e.clientX);
    },
    [setFromClientX],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (draggingRef.current) setFromClientX(e.clientX);
    },
    [setFromClientX],
  );

  const endDrag = useCallback((e: PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 10 : 2;
      let next: number | null = null;
      if (e.key === 'ArrowLeft') next = pos - step;
      else if (e.key === 'ArrowRight') next = pos + step;
      else if (e.key === 'PageDown') next = pos - 10;
      else if (e.key === 'PageUp') next = pos + 10;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = 100;
      if (next === null) return;
      e.preventDefault();
      setPos(clamp(next));
    },
    [pos],
  );

  const rounded = Math.round(pos);

  return (
    <figure className={cn('my-5', className)}>
      <div
        ref={frameRef}
        className={cn(
          'relative w-full select-none overflow-hidden rounded-lg border border-white/[0.06]',
          transparentAfter && 'checkerboard',
        )}
        style={{ aspectRatio }}
      >
        {/* Base: AFTER (visible on the right) */}
        <img
          src={afterUrl}
          alt={afterAlt}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* Overlay: BEFORE, clipped to the left `pos`% */}
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          aria-hidden="true"
        >
          <img
            src={beforeUrl}
            alt={beforeAlt}
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        <span className="pointer-events-none absolute bottom-2.5 left-2.5 rounded-full bg-canvas/70 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-primary backdrop-blur-sm">
          {beforeLabel}
        </span>
        <span className="pointer-events-none absolute bottom-2.5 right-2.5 rounded-full bg-canvas/70 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-primary backdrop-blur-sm">
          {afterLabel}
        </span>

        {/* Divider + grip */}
        <div
          className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white/90 shadow-[0_0_10px_rgba(0,0,0,0.6)]"
          style={{ left: `${pos}%` }}
          aria-hidden="true"
        >
          <span className="absolute left-1/2 top-1/2 grid h-9 w-9 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white text-canvas shadow-md">
            <ChevronsLeftRight className="h-4 w-4" aria-hidden="true" />
          </span>
        </div>

        {/* Interaction surface: pointer (mouse + touch) + keyboard */}
        <div
          role="slider"
          tabIndex={0}
          aria-label={`Drag to compare ${beforeLabel} and ${afterLabel}`}
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={rounded}
          aria-valuetext={`${rounded}% ${beforeLabel}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
          className="absolute inset-0 cursor-ew-resize touch-none focus-visible:outline-2 focus-visible:outline-cyan-400"
        />
      </div>

      {caption ? (
        <figcaption className="mt-2.5 text-center text-[13px] leading-[1.5] text-muted">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
